<template>
  <div class="[ max-w-5xl mx-auto ]">
    <div class="[ flex items-center gap-3 pb-6 border-b mb-6 ]">
      <div class="[ bg-highlight-light w-8 h-8 rounded flex items-center justify-center ]">
        <JBIcon name="user-multiple" class="[ w-5 h-5 text-xl text-highlight ]" />
      </div>
      <div class="[ font-bold text-lg ]">Customer training</div>
    </div>

    <div class="[ flex items-center pb-6 mb-6 gap-2 ]">
      <div
        class="[ bg-gray-light hover:bg-gray-mid rounded-xl p-3 flex flex-col justify-between gap-2 aspect-square max-w-32 cursor-pointer ]"
        v-for="document in documents">
        <JBIcon :name="document.icon" />
        <div class="[ text-sm font-bold ]">{{ document.name }}</div>
      </div>
    </div>

    <div>
      <DataTable :value="lessons" scrollable scroll-height="flex" :loading="loading">
        <Column field="lesson.Title" header="Title"></Column>
        <Column header="Description">
          <template #body="{ data }: { data: LessonWithRuns }">
            <span class="[ text-gray-dark ]">{{ data.lesson.Description }}</span>
          </template>
        </Column>
        <Column header="Best Score">
          <template #body="{ data }: { data: LessonWithRuns }">
            <Rating :value="data.runs[0].score!" v-if="data.runs && data.runs.length" />
            <span v-else>-</span>
          </template>
        </Column>
        <Column>
          <template #body="{ data }: { data: LessonWithRuns }">
            <ButtonComponent outlined :label="data.runs.length ? `Try again` : `Start`"
              @click="startCall(data.lesson)" />
          </template>
        </Column>
      </DataTable>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Button as ButtonComponent, DataTable, Column } from "primevue";
import { useDemoCalls } from "@/composables/demo.ts";
import { onMounted } from "vue";
import { storeToRefs } from "pinia";
import { useTrainingStore } from "@/store/contact/training.ts";
import { basePrompt } from "@/mocks/demoAIPrompts.ts";
import JBIcon from "@/components/JBIcon.vue";
import { Lesson, useApi2 } from "@/services/api2";
import { useApi, useApiLoader } from "@/services/api";
import { useAuthStore } from "@/store/auth";
import { ContactV2 } from "@/services/api/sdk";
import Rating from "@/components/ui/Rating.vue";

definePage({
  meta: {
    title: "Training",
    icon: "user-multiple",
  },
});

const { listLessonPlans, listLessons } = useApi2();
const api = useApi();

const trainingStore = useTrainingStore();
const { documents } = storeToRefs(trainingStore);
const { startAICall } = useDemoCalls();

const { agent } = useAuthStore();

type LessonWithRuns = {
  lesson: Lesson;
  runs: ContactV2[];
};

const { data: lessons, load, loading } = useApiLoader(async () => {
  const plans = await listLessonPlans();

  const res = await api.getContactsV2({
    page: 0,
    pageSize: 200,
    sortBy: 'Rating',
    desc: true,
    users: [agent?.id!],
    filterPhoneNumber: '50199941',
  });

  const lessons = await listLessons(plans[0].Id);

  return lessons.map(lesson => {
    return {
      lesson,
      runs: res.data?.filter(x => parseInt(x.customerPhoneNumber!.substring(x.customerPhoneNumber!.length - 2), 10) === lesson.Id),
    };
  });
}, []);

const startCall = async (lesson: Lesson) => {
  await startAICall(
    basePrompt + " " + lesson.Prompt,
    `(501) 999-41${lesson.Id.toString().padStart(2, '0')}`,
    "(501) 999-8889",
  );
};

onMounted(() => {
  load();
});
</script>